// Dashboard.js
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactEditList from "react-edit-list";
import { getDocs, collection, query, addDoc, orderBy, doc, deleteDoc, setDoc, where } from 'firebase/firestore/lite';
import {db} from './../firebase';
import { DASHBOARD_CRUD, notifyDashboardCrudError, failDashboardValidation, isBlank } from '../utils/dashboardCrud';
import FileUploader from './../FileUploader';
// Using native HTML5 drag and drop
import { useDragAndDrop } from './useDragAndDrop';

// Componente wrapper para buscar la foto en la fila cuando no hay url_imagen
const UrlImgRendererWrapper = ({ props, data }) => {
  const containerRef = React.useRef(null);
  const [fotoUrl, setFotoUrl] = React.useState(null);
  
  React.useEffect(() => {
    // Primero intentar obtener el ID desde props.item si está disponible
    let studentId = null;
    
    if (props.item && props.item.id) {
      studentId = props.item.id;
    } else if (containerRef.current) {
      // Si no está en props, buscar el ID del estudiante en la fila
      const row = containerRef.current.closest('tr');
      if (row) {
        // Buscar el ID en un input con name="id" o en el primer campo
        const idInput = row.querySelector('input[name="id"]');
        studentId = idInput ? idInput.value : null;
        
        // Si no hay input con name="id", buscar en el primer campo (que generalmente es el ID)
        if (!studentId) {
          const firstInput = row.querySelector('td input');
          if (firstInput && firstInput.name === 'id') {
            studentId = firstInput.value;
          } else {
            // Buscar en el texto de la primera celda si es el ID
            const cells = row.querySelectorAll('td');
            if (cells.length > 0) {
              const firstCell = cells[0];
              // El ID podría estar en el texto o en un atributo data
              studentId = firstCell.textContent?.trim() || firstCell.getAttribute('data-id');
            }
          }
        }
        
        // También verificar si hay un atributo data-id en la fila
        if (!studentId) {
          studentId = row.getAttribute('data-id') || row.getAttribute('data-row-id');
        }
      }
    }
    
    if (studentId) {
      // Buscar el estudiante en data por ID
      const student = data.find(s => s.id === studentId);
      if (student && student.foto) {
        // La foto viene de assets, construir la ruta
        try {
          const fotoPath = require(`../assets/fotos/${student.foto}`);
          setFotoUrl(fotoPath);
          
          // Llenar la columna oculta con la propiedad foto si no hay url_imagen
          // Esto se puede hacer actualizando el valor a través de props si está disponible
        } catch (e) {
          console.warn('No se pudo cargar la foto:', student.foto, e);
        }
      }
    }
  }, [data, props]);
  
  if (fotoUrl) {
    // En modo compacto, mostrar imagen más pequeña y centrada
    if (props.compactMode) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <img src={fotoUrl} alt="Foto del estudiante" style={{ maxHeight: '50px', maxWidth: '50px', height: 'auto', width: 'auto', objectFit: 'cover', borderRadius: '4px' }} />
        </div>
      );
    }
    return (
      <div>
        <div>
          <img src={fotoUrl} alt="Foto del estudiante" style={{ maxWidth: '5em', marginTop: '10px' }} />
        </div>
      </div>
    );
  }
  
  return (
    <div ref={containerRef} style={{ display: 'none' }}>
      {/* Contenedor oculto para acceder al DOM */}
    </div>
  );
};

// Schema will be created dynamically based on researchLines
const createSchema = (researchLines) => {
  // Usar ID en lugar de nombre para la relación
  // eslint-disable-next-line no-unused-vars
  const lineaOptions = researchLines.length > 0 
    ? researchLines.map(line => ({ value: line.id, name: line.name }))
    : [{ value: "", name: "Sin líneas disponibles" }];
  
  return [
  { name: "id", type: "id" },
  { name: "name", type: "string" },
  { name: "carrera", type: "string" },
  { name: "linea_investigacion", type: "custom" },
  { name: "sub_linea_investigacion", type: "custom" },
  { name: "url_imagen", type: "custom" },
  { name: "proyecto", type: "custom" },
  { name: "semblanzas", type: "custom" },
  { name: "orcidid", type: "string" },
  { name: "habilitado", type: [
      { value: true, name: "Si" },
      { value: false, name: "No" }
    ] 
  },
  /*{ name: "graduados", type: [
      { value: true, name: "Si" },
      { value: false, name: "No" }
    ] 
  }*/
];
};

const headers = {
  "name"                    : "Nombre",
  "carrera"                 : "Carrera",
  "linea_investigacion"     : "Línea de Investigación",
  "sub_linea_investigacion" : "Sub-línea de Investigación",
  "url_imagen"              : "Imagen",
  "proyecto"                : "Papers/Proyectos/Tesis",
  "semblanzas"              : "Semblanza",
  "orcidid"                 : "ORCID ID",
  "habilitado"              : "Mostrar",
  "graduados"               : "Graduado"
};

export const StudentsList = (propsRoot) => {
  
  const DOCUMENT_NAME = 'students';
  const TITLE = 'Practicantes';

  const validateStudentFields = (item, operation) => {
    const missing = [];
    if (isBlank(item.name)) missing.push('Nombre');
    if (isBlank(item.carrera)) missing.push('Carrera');
    if (missing.length === 0) return true;
    return failDashboardValidation({
      operation,
      entityName: TITLE,
      missingFields: missing,
    });
  };

  const lineIconPngToSvg = {
    'AlmacenamientoEnergiaW.png': 'AlmacenamientoEnergia.svg',
    'ConversionEnergiaW.png': 'MusculosArtificiales.svg',
    'NanomaterialesW.png': 'Nanomateriales.svg',
    'path11826.png': 'BiosensoresElectro2.svg',
    'MusculosArtificialesW.png': 'MusculosArtificiales.svg',
    'BioElectrónica.png': 'BioElectrónica.svg',
    'software.png': 'software.svg',
    'mecatronicaR.png': 'BioElectrónica.svg',
    'logotejidosR1.png': 'logotejidosR.svg',
  };
  
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const [researchLines, setResearchLines] = useState([]);
  const [subLines, setSubLines] = useState([]);
  
  // Agrupar sub-líneas por ID de línea de investigación para acceso rápido
  const subLinesByLinea = useMemo(() => {
    const grouped = {};
    subLines.forEach(subLine => {
      // subLine.linea_investigacion ahora es el ID de la línea de investigación
      const lineaId = subLine.linea_investigacion;
      if (lineaId) {
        if (!grouped[lineaId]) {
          grouped[lineaId] = [];
        }
        grouped[lineaId].push(subLine);
      }
    });
    // Ordenar cada grupo por orden
    Object.keys(grouped).forEach(lineaId => {
      grouped[lineaId].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    });
    return grouped;
  }, [subLines]);

  const applyStudentLineIcon = (item, lineaId) => {
    const selectedLine = researchLines.find(
      (line) => line.id === lineaId || line.name === lineaId
    );
    if (selectedLine?.icon) {
      let iconSvg = selectedLine.icon;
      if (iconSvg.endsWith('.png')) {
        iconSvg = lineIconPngToSvg[iconSvg] || iconSvg.replace('.png', '.svg');
      }
      item.icon_inv = iconSvg;
    } else {
      item.icon_inv = item.icon_inv || 'Nanomateriales.svg';
    }
  };

  /** Si no hay línea, asigna la primera del select; normaliza ID y actualiza icono. */
  const ensureStudentLineaInvestigacion = (item, operation) => {
    if (researchLines.length === 0) {
      return failDashboardValidation({
        operation,
        entityName: TITLE,
        missingFields: ['Línea de investigación'],
        message: 'No hay líneas de investigación disponibles. Registra al menos una en el dashboard.',
      });
    }
    if (isBlank(item.linea_investigacion)) {
      item.linea_investigacion = researchLines[0].id;
    } else {
      const match = researchLines.find(
        (line) => line.id === item.linea_investigacion || line.name === item.linea_investigacion
      );
      item.linea_investigacion = match ? match.id : researchLines[0].id;
    }
    applyStudentLineIcon(item, item.linea_investigacion);
    if (item.sub_linea_investigacion && item.linea_investigacion) {
      const subLineasForLine = subLinesByLinea[item.linea_investigacion] || [];
      const subLineaExists = subLineasForLine.some(
        (sub) => sub.id === item.sub_linea_investigacion
      );
      if (!subLineaExists) {
        item.sub_linea_investigacion = null;
      }
    }
    return true;
  };
  
  // Crear un mapa de nombres de líneas a IDs para conversión
  // eslint-disable-next-line no-unused-vars
  const lineaNameToId = useMemo(() => {
    const map = {};
    researchLines.forEach(line => {
      map[line.name] = line.id;
      map[line.id] = line.id; // También mapear ID a ID para compatibilidad
    });
    return map;
  }, [researchLines]);
  
  // Crear un mapa de IDs de líneas a nombres para mostrar (no usado actualmente)
  // eslint-disable-next-line no-unused-vars
  const lineaIdToName = useMemo(() => {
    const map = {};
    researchLines.forEach(line => {
      map[line.id] = line.name;
    });
    return map;
  }, [researchLines]);
  const [compactMode, setCompactMode] = useState(false);

  const navigate = useNavigate();

  const goToDashboard = async () => {
    navigate('/dashboard');
  };

  // Keep dataRef in sync with data state
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchStudents = async () => {
    try {
      const q = query(collection(db, "students"), orderBy('orden'));
      const querySnapshot = await getDocs(q);
      const newData = querySnapshot.docs
        .map((doc) => {
          const studentData = { ...doc.data(), id: doc.id };
          // Asegurar que sub_linea_investigacion siempre esté presente (puede ser null o vacío)
          if (studentData.sub_linea_investigacion === undefined) {
            studentData.sub_linea_investigacion = null;
          }
          if (studentData.orcidid === undefined) {
            studentData.orcidid = null;
          }
          return studentData;
        });
      setData(newData);
      dataRef.current = newData;
      // El hook useDragAndDrop se encarga automáticamente de inicializar el drag and drop
    } catch (error) {
      notifyDashboardCrudError({ operation: DASHBOARD_CRUD.LOAD, entityName: TITLE, error });
      setData([]);
    }
  }

  const ref = useRef(null);
  
  // Use drag and drop hook
  const { isSaving } = useDragAndDrop(data, setData, dataRef, DOCUMENT_NAME, TITLE, ref, fetchStudents);

  const fetchResearchLines = async () => {
    try {
      const q = query(collection(db, "research-lines"), orderBy('order'));
      const querySnapshot = await getDocs(q);
      const newData = querySnapshot.docs
        .map((doc) => ({ ...doc.data(), id:doc.id }));
      setResearchLines(newData);
    } catch (error) {
      notifyDashboardCrudError({ operation: DASHBOARD_CRUD.LOAD, entityName: 'Líneas de investigación', error });
      setResearchLines([]);
    }
  }

  const fetchSubLines = async () => {
    try {
      const q = query(collection(db, "sub-research-lines"), where("enabled", "==", true), orderBy('orden'));
      const querySnapshot = await getDocs(q);
      const newData = querySnapshot.docs
        .map((doc) => ({ ...doc.data(), id:doc.id }));
      console.log('[StudentsList] Sub-líneas cargadas:', newData);
      setSubLines(newData);
    } catch (error) {
      notifyDashboardCrudError({ operation: DASHBOARD_CRUD.LOAD, entityName: 'Sub-líneas de investigación', error });
      try {
        const q2 = query(collection(db, "sub-research-lines"), where("enabled", "==", true));
        const querySnapshot2 = await getDocs(q2);
        const newData2 = querySnapshot2.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .sort((a, b) => (a.orden || 0) - (b.orden || 0));
        setSubLines(newData2);
      } catch (error2) {
        notifyDashboardCrudError({
          operation: DASHBOARD_CRUD.LOAD,
          entityName: 'Sub-líneas de investigación',
          error: error2,
          context: 'Reintento sin orderBy.',
        });
        setSubLines([]);
      }
    }
  }

  useEffect(()=>{
    fetchResearchLines();
    fetchSubLines();
    fetchStudents();
  }, [])

  // Apply minimum width to proyecto and semblanzas columns using CSS
  useEffect(() => {
    const styleId = 'students-list-column-styles';
    let existingStyle = document.getElementById(styleId);
    
    const updateStyles = () => {
      // Calculate main header height dynamically
      const mainHeader = document.querySelector('.table-responsive .table thead');
      // eslint-disable-next-line no-unused-vars
      const mainHeaderHeight = mainHeader ? mainHeader.offsetHeight : 0;
      
      if (existingStyle) {
        existingStyle.remove();
      }
      
      const style = document.createElement('style');
      style.id = styleId;
      // proyecto is 7th column (after id, name, icon_inv, carrera, linea_investigacion, url_imagen)
      // semblanzas is 8th column
      const compactStyles = compactMode ? `
      /* Compact mode styles - Solo mostrar nombre y foto en una línea */
      .table-responsive.compact-mode .table tbody tr {
        padding: 0.25rem 0 !important;
        line-height: 1.5 !important;
        min-height: auto !important;
        max-height: 60px !important;
        height: 60px !important;
        white-space: nowrap !important;
      }
      .table-responsive.compact-mode .table tbody tr td {
        padding: 0.5rem !important;
        font-size: 0.875rem !important;
        line-height: 1.5 !important;
        vertical-align: middle !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        height: 60px !important;
        max-height: 60px !important;
      }
      .table-responsive.compact-mode .table thead th {
        padding: 0.5rem 0.5rem !important;
        font-size: 0.875rem !important;
        line-height: 1.2 !important;
      }
      /* Ocultar todas las columnas excepto nombre (2da columna) y foto (6ta columna) */
      /* Orden: 1=name, 2=carrera, 3=linea_investigacion, 4=sub_linea_investigacion, 5=url_imagen, 6=proyecto, 7=semblanzas, 8=habilitado */
      .table-responsive.compact-mode .table thead th:nth-child(3),
      .table-responsive.compact-mode .table tbody td:nth-child(3),
      .table-responsive.compact-mode .table thead th:nth-child(4),
      .table-responsive.compact-mode .table tbody td:nth-child(4),
      .table-responsive.compact-mode .table thead th:nth-child(6),
      .table-responsive.compact-mode .table tbody td:nth-child(6),
      .table-responsive.compact-mode .table thead th:nth-child(7),
      .table-responsive.compact-mode .table tbody td:nth-child(7),
      .table-responsive.compact-mode .table thead th:nth-child(8),
      .table-responsive.compact-mode .table tbody td:nth-child(8),
      .table-responsive.compact-mode .table thead th:nth-child(9),
      .table-responsive.compact-mode .table tbody td:nth-child(9) {
        display: none !important;
      }
      /* Estilos para nombre (2da columna) */
      .table-responsive.compact-mode .table thead th:nth-child(2),
      .table-responsive.compact-mode .table tbody td:nth-child(2) {
        display: table-cell !important;
        max-width: 400px !important;
        min-width: 200px !important;
        width: auto !important;
        padding: 0.5rem !important;
      }
      .table-responsive.compact-mode .table tbody td:nth-child(2) span,
      .table-responsive.compact-mode .table tbody td:nth-child(2) input {
        max-width: 100% !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        display: block !important;
      }
      /* Estilos para foto (6ta columna) */
      .table-responsive.compact-mode .table thead th:nth-child(5),
      .table-responsive.compact-mode .table tbody td:nth-child(5) {
        display: table-cell !important;
        width: 80px !important;
        min-width: 80px !important;
        max-width: 80px !important;
        text-align: center !important;
        padding: 0.25rem !important;
      }
      .table-responsive.compact-mode .table tbody td:nth-child(5) img {
        max-height: 50px !important;
        max-width: 50px !important;
        height: auto !important;
        width: auto !important;
        object-fit: cover !important;
        border-radius: 4px !important;
        margin: 0 !important;
      }
      .table-responsive.compact-mode .table tbody td:nth-child(5) > div {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
      }
      .table-responsive.compact-mode .table tbody td input,
      .table-responsive.compact-mode .table tbody td select,
      .table-responsive.compact-mode .table tbody td textarea {
        padding: 0.25rem 0.5rem !important;
        font-size: 0.875rem !important;
        line-height: 1.2 !important;
      }
      .table-responsive.compact-mode .card-body {
        padding: 0.5rem !important;
      }
      ` : '';
      
      // Checkbox styles - always applied, color changes based on compactMode
      const checkboxColor = compactMode ? '#28a745' : '#0d6efd';
      const checkboxFocusColor = compactMode ? '#218838' : '#0a58ca';
      const checkboxBorderColor = compactMode ? '#1e7e34' : '#0a58ca';
      const checkboxShadow = compactMode ? 'rgba(40, 167, 69, 0.25)' : 'rgba(13, 110, 253, 0.25)';
      
      style.textContent = `
      .table-responsive {
        overflow-y: visible;
        overflow-x: auto;
        width: 100% !important;
      }
      .table-responsive .table {
        width: 100% !important;
        margin-bottom: 0;
        table-layout: auto;
        flex-shrink: 0;
      }
      .card-body {
        width: 100% !important;
        padding: 1rem !important;
      }
      .table-responsive .table thead {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: #0d6efd;
      }
      .table-responsive .table thead th {
        position: sticky;
        top: 0;
        z-index: 11;
        background-color: #0d6efd !important;
        color: white !important;
        border-color: rgba(255, 255, 255, 0.2) !important;
      }
      .table-responsive .table thead th:nth-child(6),
      .table-responsive .table tbody td:nth-child(6) {
        min-width: 800px !important;
        width: auto !important;
      }
      .table-responsive .table thead th:nth-child(7),
      .table-responsive .table tbody td:nth-child(7) {
        min-width: 400px !important;
        width: auto !important;
      }
      /* Nested tables headers - sticky but below main header */
      .table-responsive .table tbody td .table {
        max-height: 400px;
        overflow-y: auto;
        display: block;
        position: relative;
        isolation: isolate;
      }
      .table-responsive .table tbody td .table thead {
        position: sticky !important;
        top: 0 !important;
        z-index: 100 !important;
        background-color: #212529 !important;
        isolation: isolate;
      }
      .table-responsive .table tbody td .table thead th {
        position: sticky !important;
        top: 0 !important;
        z-index: 99 !important;
        background-color: #212529 !important;
        color: white !important;
        border-color: rgba(255, 255, 255, 0.2) !important;
      }
      .table-responsive .table tbody td .table thead tr {
        background-color: #212529 !important;
        position: relative;
        z-index: 100 !important;
      }
      .table-responsive .table tbody td .table tbody {
        position: relative;
        z-index: 0 !important;
      }
      .table-responsive .table tbody td .table tbody tr {
        position: relative;
        z-index: 0 !important;
        background-color: inherit;
      }
      .table-responsive .table tbody td .table tbody td {
        position: relative;
        z-index: 0 !important;
        background-color: inherit;
      }
      /* Ensure nested table cells don't interfere */
      .table-responsive .table tbody td {
        position: relative;
        vertical-align: top;
      }
      /* Native HTML5 drag and drop styles */
      .table-responsive .table tbody tr.draggable {
        cursor: grab !important;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        transition: background-color 0.2s;
      }
      .table-responsive .table tbody tr.draggable:active {
        cursor: grabbing !important;
      }
      .table-responsive .table tbody tr.draggable.dragging {
        opacity: 0.5;
        background-color: #e9ecef !important;
      }
      .table-responsive .table tbody tr.draggable.drag-over {
        border-top: 2px solid #0d6efd;
        background-color: #f0f7ff !important;
      }
      /* Make sure draggable rows can be dragged */
      .table-responsive .table tbody tr.draggable td {
        cursor: inherit;
      }
      /* Prevent drag on interactive elements */
      .table-responsive .table tbody tr.draggable input,
      .table-responsive .table tbody tr.draggable button,
      .table-responsive .table tbody tr.draggable a,
      .table-responsive .table tbody tr.draggable select {
        cursor: default;
      }
      /* Checkbox styles - color changes based on compactMode state */
      .form-check-input#compactModeCheck:checked {
        background-color: ${checkboxColor} !important;
        border-color: ${checkboxColor} !important;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e") !important;
      }
      .form-check-input#compactModeCheck:checked:focus {
        background-color: ${checkboxFocusColor} !important;
        border-color: ${checkboxBorderColor} !important;
        box-shadow: 0 0 0 0.25rem ${checkboxShadow} !important;
      }
      .form-check-input#compactModeCheck.compact-mode-active:checked {
        background-color: #28a745 !important;
        border-color: #28a745 !important;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e") !important;
      }
      .form-check-input#compactModeCheck.compact-mode-active:checked:focus {
        background-color: #218838 !important;
        border-color: #1e7e34 !important;
        box-shadow: 0 0 0 0.25rem rgba(40, 167, 69, 0.25) !important;
      }
      ${compactStyles}
    `;
      document.head.appendChild(style);
      existingStyle = style;
    };

    // Initial update
    updateStyles();

    // Update on window resize and after a short delay to ensure table is rendered
    const timeoutId = setTimeout(updateStyles, 100);
    window.addEventListener('resize', updateStyles);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateStyles);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [data, compactMode]);

  // Apply checkbox color directly when compactMode changes
  useEffect(() => {
    const checkbox = document.getElementById('compactModeCheck');
    if (!checkbox) return;
    
    const applyColor = () => {
      if (checkbox.checked) {
        const bgColor = compactMode ? '#28a745' : '#0d6efd';
        const borderColor = compactMode ? '#28a745' : '#0d6efd';
        
        // Create a style element with higher specificity
        let styleEl = document.getElementById('compactModeCheckStyle');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'compactModeCheckStyle';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = `
          #compactModeCheck.form-check-input:checked {
            background-color: ${bgColor} !important;
            border-color: ${borderColor} !important;
          }
        `;
      }
    };
    
    // Apply immediately
    applyColor();
    
    // Also listen for changes to the checkbox
    const handleChange = applyColor;
    checkbox.addEventListener('change', handleChange);
    
    return () => {
      checkbox.removeEventListener('change', handleChange);
      const styleEl = document.getElementById('compactModeCheckStyle');
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [compactMode]);

  return (
    <div className="container-fluid mt-1">
        <div className="card shadow">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <button className="btn btn-danger" onClick={goToDashboard} >Regresar</button>
                <h3 className="mb-0">Listado de Practicantes</h3>
                <div className="d-flex align-items-center gap-3">
                  <div className="form-check form-check-inline">
                    <input 
                      className={`form-check-input ${compactMode ? 'compact-mode-active' : ''}`}
                      type="checkbox" 
                      id="compactModeCheck"
                      checked={compactMode}
                      onChange={(e) => setCompactMode(e.target.checked)}
                      style={{ 
                        cursor: 'pointer'
                      }}
                    />
                    <label className="form-check-label text-white" htmlFor="compactModeCheck" style={{ cursor: 'pointer' }}>
                      Modo compacto para reordenar
                    </label>
                  </div>
                {isSaving && (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-light me-2" role="status">
                      <span className="visually-hidden">Guardando...</span>
                    </div>
                    <span className="text-light">Guardando cambios...</span>
                  </div>
                )}
                </div>
            </div>
            <div className="card-body" style={{ padding: '1rem', width: '100%' }}>
                <div className={`table-responsive ${compactMode ? 'compact-mode' : ''}`} style={{ overflowX: 'auto', width: '100%' }}>
                    <React.Fragment>
                      <div className="m-3 fw-bold">
                        {(true)?"":"Se pueden reordenar los practicantes moviendolos de orden en la lista"}
                      </div>
                      <ReactEditList
                        key={researchLines.length} // Force re-render when researchLines change
                        schema={createSchema(researchLines)}
                        headers={headers}
                        onLoad={() => data}
                        ref={ref}
                        format={{
                          name: function NameRenderer(props) {
                            // En modo compacto, mostrar solo el nombre sin formato adicional
                            if (compactMode) {
                              return <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '100%' }}>{props.value || ''}</span>;
                            }
                            return <span>{props.value || ''}</span>;
                          },
                          proyecto: function ProyectoRenderer(props) {
                            if ( props.value !== "" && props.value && props.value.length > 0 )
                            {
                              const MAX_LENGTH = 100;
                              const MAX_PROJECTS = 3;
                              const truncateText = (text) => {
                                if (!text) return '';
                                return text.length > MAX_LENGTH ? text.substring(0, MAX_LENGTH) + '...' : text;
                              };
                              // Ordenar: primero los principales
                              const sortedProjects = [...props.value].sort((a, b) => {
                                if (a.isMain === true && b.isMain !== true) return -1;
                                if (a.isMain !== true && b.isMain === true) return 1;
                                return 0;
                              });
                              const displayedProjects = sortedProjects.slice(0, MAX_PROJECTS);
                              const hasMore = sortedProjects.length > MAX_PROJECTS;
                              return (
                                <ul style={{ minWidth: '300px', wordWrap: 'break-word' }}>
                                  {displayedProjects.map((item, idx) => (
                                    <li key={idx} style={{ marginBottom: '5px' }}>
                                      {item.isMain === true && <span className="badge bg-primary me-2">Principal</span>}
                                      {item.url ? (
                                        <a href={item.url} target="_blank" rel="noopener noreferrer" title={item.proyecto}>
                                          {truncateText(item.proyecto)}
                                        </a>
                                      ) : (
                                        <span title={item.proyecto}>{truncateText(item.proyecto)}</span>
                                      )}
                                    </li>
                                  ))}
                                  {hasMore && (
                                    <li style={{ marginTop: '5px', fontStyle: 'italic', color: '#666' }}>
                                      ... y {sortedProjects.length - MAX_PROJECTS} más
                                    </li>
                                  )}
                                </ul>
                              );
                            }
                            else
                            {
                              return <ul></ul>;
                            }
                          },
                          semblanzas: function SemblanzasRenderer(props) {
                            if ( props.value && props.value.length > 0 )
                            {
                              const MAX_LENGTH = 150;
                              const truncateText = (text) => {
                                if (!text) return '';
                                return text.length > MAX_LENGTH ? text.substring(0, MAX_LENGTH) + '...' : text;
                              };
                              return (
                                <div style={{ minWidth: '300px', wordWrap: 'break-word' }}>
                                  {props.value.map((semblanza, idx) => (
                                    <p key={idx} style={{ marginBottom: '8px' }} title={semblanza.descripcion}>
                                      {truncateText(semblanza.descripcion)}
                                    </p>
                                  ))}
                                </div>
                              );
                            }
                            else
                            {
                              return null;
                            }
                          },
                          url_imagen: function UrlImgRenderer(props) {
                            if ( props.value )
                            {
                              // En modo compacto, solo mostrar la imagen sin el enlace
                              if (compactMode) {
                                return (
                                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <img src={props.value} alt="Foto del estudiante" style={{ maxHeight: '50px', maxWidth: '50px', height: 'auto', width: 'auto', objectFit: 'cover', borderRadius: '4px' }} />
                                  </div>
                                );
                              }
                              return (
                                <div>
                                  <a href={props.value} target="_blank" rel="noopener noreferrer">Descargar</a>
                                  <div>
                                    <img src={props.value} alt="Archivo Subido" style={{ maxWidth: '5em', marginTop: '10px' }} />
                                  </div>
                                </div>
                              );
                            }
                            else
                            {
                              // Componente wrapper para buscar la foto en la fila
                              return React.createElement(UrlImgRendererWrapper, { 
                                props, 
                                data,
                                compactMode 
                              });
                            }
                          },
                          linea_investigacion: function LineaRenderer(props) {
                            if (props.value) {
                              // props.value ahora es un ID, buscar el nombre
                              const linea = researchLines.find(l => l.id === props.value);
                              if (linea) {
                                return <span>{linea.name}</span>;
                              }
                              // Fallback: mostrar el valor directamente (compatibilidad con datos antiguos)
                              return <span>{props.value}</span>;
                            }
                            if (researchLines.length > 0) {
                              return <span>{researchLines[0].name}</span>;
                            }
                            return <span style={{ color: '#999', fontStyle: 'italic' }}>Sin línea</span>;
                          },
                          sub_linea_investigacion: function SubLineaRenderer(props) {
                            if (props.value) {
                              // props.value ahora es un ID de sub-línea, buscar el título
                              const subLinea = subLines.find(sl => sl.id === props.value);
                              if (subLinea) {
                                return <span>{subLinea.titulo}</span>;
                              }
                              // Fallback: mostrar el valor directamente (compatibilidad con datos antiguos)
                              return <span>{props.value}</span>;
                            }
                            return <span style={{ color: '#999', fontStyle: 'italic' }}>Sin sub-línea</span>;
                          },
                        }}
                        edit={{
                          // You have to provide a custom editor that is a React component
                          // react-edit-list will pass you the current value in `props.value`
                          // In order to modify it, you will have to call `props.onChange()`
                          proyecto: function ProyectoEditor(props) {
                            const subSchema = [
                              { name: "type", type: propsRoot.projectTypes.map((item) => ({name: item.title, value: item.type}) ) },
                              { name: "url", type: "string" },
                              { name: "proyecto", type: "string" },
                              { name: "isMain", type: [
                                  { value: true, name: "Principal" },
                                  { value: false, name: "No principal" }
                                ] 
                              }
                            ];
                            const MAX_LENGTH = 100;
                            const truncateText = (text) => {
                              if (!text) return '';
                              return text.length > MAX_LENGTH ? text.substring(0, MAX_LENGTH) + '...' : text;
                            };
                            
                            // Función para obtener la lista de proyectos para el selector principal
                            const getProjectsForMainSelector = () => {
                              return props.value ? props.value.map((p, idx) => ({
                                value: idx,
                                name: truncateText(p.proyecto || `Proyecto ${idx + 1}`)
                              })) : [];
                            };
                            
                            return (
                              <div>
                                {/* Selector de paper principal */}
                                <div className="mb-3 p-2 border rounded">
                                  <label className="form-label fw-bold">Paper/Artículo Principal:</label>
                                  <select 
                                    className="form-select"
                                    value={props.value ? props.value.findIndex(p => p.isMain === true) : -1}
                                    onChange={(e) => {
                                      const selectedIndex = parseInt(e.target.value);
                                      const values = [...(props.value || [])];
                                      
                                      // Desmarcar todos como principales
                                      values.forEach(p => { p.isMain = false; });
                                      
                                      // Marcar el seleccionado como principal
                                      if (selectedIndex >= 0 && selectedIndex < values.length) {
                                        values[selectedIndex].isMain = true;
                                      }
                                      
                                      props.onChange(values);
                                    }}
                                  >
                                    <option value="-1">Ninguno</option>
                                    {getProjectsForMainSelector().map((option, idx) => (
                                      <option key={idx} value={option.value}>
                                        {option.name}
                                      </option>
                                    ))}
                                  </select>
                                  <small className="text-muted">Solo se puede seleccionar un paper/articulo como principal</small>
                                </div>
                                
                                {/* Lista de proyectos */}
                                <ReactEditList
                                  schema={subSchema}
                                  onLoad={() => props.value ? props.value : []  }
                                  format={{
                                    proyecto: function ProyectoSubRenderer(subProps) {
                                      if (subProps.value) {
                                        const isMain = subProps.item?.isMain === true;
                                        return (
                                          <span title={subProps.value} style={{ display: 'block', maxWidth: '400px', wordWrap: 'break-word' }}>
                                            {isMain && <span className="badge bg-primary me-2">Principal</span>}
                                            {truncateText(subProps.value)}
                                          </span>
                                        );
                                      }
                                      return null;
                                    }
                                  }}
                                  onUpdate={(item, old) => {
                                    // Call your API here
                                    if (isBlank(item.proyecto)) {
                                      return failDashboardValidation({
                                        operation: DASHBOARD_CRUD.UPDATE,
                                        entityName: 'Proyecto del practicante',
                                        missingFields: ['Proyecto'],
                                        message: 'Debes capturar la descripción del proyecto o paper.',
                                      });
                                    }
                                    if ( !item.type )
                                    {
                                      item.type = propsRoot.projectTypes.lenght > 0 ? propsRoot.projectTypes[0]: null;
                                    }
                                    if ( !item.url )
                                    {
                                      item.url = "";
                                    }
                                    // Si se marca como principal, desmarcar los demás
                                    if (item.isMain === true) {
                                      props.value.forEach((p, idx) => {
                                        if (p.proyecto !== old.proyecto) {
                                          p.isMain = false;
                                        }
                                      });
                                    }
                                    // Si no se especifica isMain, mantener el valor actual o false
                                    if (item.isMain === undefined) {
                                      const oldItem = props.value.find(p => p.proyecto === old.proyecto);
                                      item.isMain = oldItem?.isMain || false;
                                    }
                                    const fItem = props.value.find( (value) => value.proyecto === old.proyecto);
                                    const index = props.value.indexOf(fItem);
                                    props.value[index] = item;
                                    props.onChange(props.value);
                                  }}
                                  onDelete={(old) => {
                                    if (!window.confirm("Are you sure you want to delete it?")) return false;
                                    // Call your API here
                                    const values = [...props.value];
                                    const fItem = props.value.find( (value) => value.proyecto === old.proyecto);
                                    const index = props.value.indexOf(fItem);
                                    values.splice(index, 1);
                                    props.onChange(values);
                                  }}
                                  onInsert={(item) => {
                                    // Call your API here
                                    const values = [...props.value];
                                    if (isBlank(item.proyecto)) {
                                      return failDashboardValidation({
                                        operation: DASHBOARD_CRUD.CREATE,
                                        entityName: 'Proyecto del practicante',
                                        missingFields: ['Proyecto'],
                                        message: 'Debes capturar la descripción del proyecto o paper.',
                                      });
                                    }
                                    if ( item.type === undefined )
                                    {
                                      item.type = propsRoot.projectTypes.length > 0 ? propsRoot.projectTypes[0].type : null;
                                    }
                                    if ( item.url === undefined )
                                    {
                                      item.url = "";
                                    }
                                    // Por defecto, no es principal
                                    if ( item.isMain === undefined )
                                    {
                                      item.isMain = false;
                                    }
                                    // Si se marca como principal, desmarcar los demás
                                    if (item.isMain === true) {
                                      values.forEach(p => { p.isMain = false; });
                                    }
                                    values.push(item);
                                    props.onChange(values);
                                  }}
                                  className="table table-light table-fixed align-middle"
                                  headClassName="table-dark"
                                  inputClassName="w-100"
                                  btnValidateClassName="btn btn-success p-0 m-0"
                                  btnDeleteClassName="btn btn-danger py-0 px-1 m-0 mx-1"
                                  btnCancelClassName="btn btn-secondary py-0 px-1 m-0 mx-1"
                                />
                              </div>
                            );
                          },
                          semblanzas: function SemblanzasEditor(props) {
                            const subSchema = [
                              { name: "descripcion", type: "string" }
                            ];
                            const MAX_LENGTH = 150;
                            const truncateText = (text) => {
                              if (!text) return '';
                              return text.length > MAX_LENGTH ? text.substring(0, MAX_LENGTH) + '...' : text;
                            };
                            return (
                              // This is a simple Bootstrap 5 dropdown with manual control
                              <ReactEditList
                                schema={subSchema}
                                onLoad={() => props.value ? props.value : []  }
                                format={{
                                  descripcion: function DescripcionSubRenderer(subProps) {
                                    if (subProps.value) {
                                      return (
                                        <span title={subProps.value} style={{ display: 'block', maxWidth: '400px', wordWrap: 'break-word' }}>
                                          {truncateText(subProps.value)}
                                        </span>
                                      );
                                    }
                                    return null;
                                  }
                                }}
                                onUpdate={(item, old) => {
                                  // Call your API here
                                  const fItem = props.value.find( (value) => value.descripcion === old.descripcion);
                                  const index = props.value.indexOf(fItem);
                                  props.value[index] = item;
                                  props.onChange(props.value);
                                }}
                                onDelete={(old) => {
                                  if (!window.confirm("Are you sure you want to delete it?")) return false;
                                  // Call your API here
                                  const values = [...props.value];
                                  const fItem = props.value.find( (value) => value.descripcion === old.descripcion);
                                  const index = props.value.indexOf(fItem);
                                  values.splice(index, 1);
                                  props.onChange(values);
                                }}
                                onInsert={(item) => {
                                  // Call your API here
                                  const values = [...props.value];
                                  if (isBlank(item.descripcion)) {
                                    return failDashboardValidation({
                                      operation: DASHBOARD_CRUD.CREATE,
                                      entityName: 'Semblanza',
                                      missingFields: ['Descripción'],
                                      message: 'Debes capturar el texto de la semblanza.',
                                    });
                                  }
                                  values.push(item);
                                  props.onChange(values);
                                }}
                                className="table table-light table-fixed align-middle"
                                headClassName="table-dark"
                                inputClassName="w-100"
                                btnValidateClassName="btn btn-success p-0 m-0"
                                btnDeleteClassName="btn btn-danger py-0 px-1 m-0 mx-1"
                                btnCancelClassName="btn btn-secondary py-0 px-1 m-0 mx-1"
                              />
                            );
                          },
                          url_imagen: function UrlImgEditor(props) {
                            return (<FileUploader url={props.value} onUpload={(file)=>{
                              props.onChange(file);
                            }}/>);
                          },
                          linea_investigacion: function LineaInvestigacionEditor(props) {
                              const defaultLineaId = researchLines.length > 0 ? researchLines[0].id : '';
                              return <select 
                                className="form-select" 
                                name="linea_investigacion"
                                value={props.value || defaultLineaId}
                                onChange={(e) => {
                                  props.onChange(e.target.value);
                                  // Disparar evento personalizado para notificar al select de sub-línea
                                  const row = e.target.closest('tr');
                                  if (row) {
                                    const customEvent = new CustomEvent('lineaInvestigacionChanged', {
                                      detail: { lineaId: e.target.value },
                                      bubbles: true
                                    });
                                    row.dispatchEvent(customEvent);
                                  }
                                }}
                              >
                                {researchLines.map((linea)=>(
                                  <option key={linea.id} value={linea.id}>{linea.name}</option>
                                ))}
                              </select>;
                          },
                          sub_linea_investigacion: function SubLineaEditorWrapperFunc(props) {
                            const selectRef = useRef(null);
                            const [currentLinea, setCurrentLinea] = useState(null);
                            
                            // Función para encontrar la línea de investigación en la misma fila
                            const findLineaInRow = React.useCallback(() => {
                              if (!selectRef.current) return null;
                              const row = selectRef.current.closest('tr');
                              if (row) {
                                const lineaSelect = row.querySelector('select[name="linea_investigacion"]');
                                if (lineaSelect) {
                                  const value = lineaSelect.value;
                                  if (value) {
                                    return value;
                                  }
                                  // También verificar la opción seleccionada
                                  const selectedOption = lineaSelect.options[lineaSelect.selectedIndex];
                                  if (selectedOption && selectedOption.value) {
                                    return selectedOption.value;
                                  }
                                }
                              }
                              return null;
                            }, []);

                            // Buscar la línea de investigación cuando el componente se monta o cuando cambia
                            useEffect(() => {
                              const findLinea = () => {
                                const linea = findLineaInRow();
                                if (linea && linea !== currentLinea) {
                                  setCurrentLinea(linea);
                                }
                              };
                              
                              // Intentar inmediatamente
                              findLinea();
                              
                              // Intentar con delays para asegurar que el DOM esté listo
                              const timeouts = [10, 50, 100, 200].map(delay => 
                                setTimeout(findLinea, delay)
                              );
                              
                              // Escuchar el evento personalizado cuando cambia la línea de investigación
                              const handleLineaChange = (e) => {
                                if (selectRef.current) {
                                  const row = selectRef.current.closest('tr');
                                  if (row && row === e.target) {
                                    const lineaId = e.detail?.lineaId;
                                    if (lineaId && lineaId !== currentLinea) {
                                      setCurrentLinea(lineaId);
                                    }
                                  }
                                }
                              };
                              
                              // Escuchar cambios en el select de línea de investigación (fallback)
                              const handleLineChange = (e) => {
                                if (e.target.name === 'linea_investigacion') {
                                  if (selectRef.current) {
                                    const row = selectRef.current.closest('tr');
                                    if (row && row.contains(e.target)) {
                                      setCurrentLinea(e.target.value);
                                    }
                                  }
                                }
                              };
                              
                              // Escuchar clicks en la fila para detectar cuando entra en modo edición
                              const handleClick = (e) => {
                                if (selectRef.current) {
                                  const row = selectRef.current.closest('tr');
                                  if (row && row.contains(e.target)) {
                                    setTimeout(findLinea, 10);
                                  }
                                }
                              };
                              
                              // Agregar listeners
                              if (selectRef.current) {
                                const row = selectRef.current.closest('tr');
                                if (row) {
                                  row.addEventListener('lineaInvestigacionChanged', handleLineaChange);
                                }
                              }
                              
                              document.addEventListener('change', handleLineChange, true);
                              document.addEventListener('click', handleClick, true);
                              
                              // MutationObserver para detectar cambios en el DOM
                              const observer = new MutationObserver(() => {
                                findLinea();
                              });
                              
                              if (selectRef.current) {
                                const row = selectRef.current.closest('tr');
                                if (row) {
                                  observer.observe(row, {
                                    childList: true,
                                    subtree: true,
                                    attributes: true,
                                    attributeFilter: ['value', 'selected']
                                  });
                                }
                              }
                              
                              // Intervalo para buscar periódicamente cuando no hay línea
                              const intervalId = setInterval(() => {
                                if (!currentLinea && selectRef.current) {
                                  findLinea();
                                }
                              }, 200);
                              
                              const selectElementForCleanup = selectRef.current;
                              return () => {
                                timeouts.forEach(clearTimeout);
                                clearInterval(intervalId);
                                observer.disconnect();
                                if (selectElementForCleanup) {
                                  const row = selectElementForCleanup.closest('tr');
                                  if (row) {
                                    row.removeEventListener('lineaInvestigacionChanged', handleLineaChange);
                                  }
                                }
                                document.removeEventListener('change', handleLineChange, true);
                                document.removeEventListener('click', handleClick, true);
                              };
                            }, [findLineaInRow, currentLinea]);
                            
                            // eslint-disable-next-line react-hooks/exhaustive-deps
                            // Obtener las sub-líneas filtradas por la línea de investigación actual
                            const filteredSubLines = React.useMemo(() => {
                              if (!currentLinea || !subLines || subLines.length === 0) {
                                return [];
                              }
                              return subLines.filter(subLinea => subLinea.linea_investigacion === currentLinea);
                              // eslint-disable-next-line react-hooks/exhaustive-deps
                            }, [currentLinea]);

                            return (
                              <select 
                                ref={selectRef}
                                className="form-select" 
                                name="sub_linea_investigacion"
                                value={props.value || ""}
                                onChange={(e) => {
                                  props.onChange(e.target.value || null);
                                }}
                                disabled={!currentLinea || filteredSubLines.length === 0}
                                style={{ minWidth: '200px' }}
                                onFocus={() => {
                                  const linea = findLineaInRow();
                                  if (linea && linea !== currentLinea) {
                                    setCurrentLinea(linea);
                                  }
                                }}
                                onClick={() => {
                                  const linea = findLineaInRow();
                                  if (linea && linea !== currentLinea) {
                                    setCurrentLinea(linea);
                                  }
                                }}
                              >
                                <option value="">Sin Sub-Línea</option>
                                {filteredSubLines.map((subLinea) => (
                                  <option key={subLinea.id} value={subLinea.id}>
                                    {subLinea.titulo}
                                  </option>
                                ))}
                              </select>
                            );
                          }
                        }}
                        onUpdate={async (item) => {
                          // Call your API here
                          if (validateStudentFields(item, DASHBOARD_CRUD.UPDATE) === false) {
                            return false;
                          }
                          if (ensureStudentLineaInvestigacion(item, DASHBOARD_CRUD.UPDATE) === false) {
                            return false;
                          }
                          if (!item.proyecto)
                          {
                            item.proyecto = [];
                          }
                          if (!item.semblanzas || item.semblanzas === undefined)
                          {
                            item.semblanzas = [];
                          }
                          if (!item.url_imagen)
                          {
                            item.url_imagen = null;
                          }
                          // Asegurar que sub_linea_investigacion siempre esté presente (puede ser null o vacío)
                          if (item.sub_linea_investigacion === undefined) {
                            item.sub_linea_investigacion = null;
                          }
                          if (typeof item.orcidid === 'string') {
                            item.orcidid = item.orcidid.trim() || null;
                          } else if (item.orcidid === undefined || item.orcidid === '') {
                            item.orcidid = null;
                          }
                          item.habilitado = item.habilitado === 'false' || item.habilitado === false ? false : true;
                          if (item.graduados === undefined || item.graduados === null) {
                            item.graduados = false;
                          } else {
                            item.graduados = item.graduados === 'false' || item.graduados === false ? false : true;
                          }
                          try {
                            await setDoc(doc(db, "students", item.id), item);
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.UPDATE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        onDelete={async (item) => {
                          if (!window.confirm("Are you sure you want to delete it?")) return false;
                          // Call your API here
                          try {
                            await deleteDoc(doc(db, "students", item.id));
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.DELETE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        onInsert={ async (item) => {
                          // Call your API here
                          if (validateStudentFields(item, DASHBOARD_CRUD.CREATE) === false) {
                            return false;
                          }
                          if (ensureStudentLineaInvestigacion(item, DASHBOARD_CRUD.CREATE) === false) {
                            return false;
                          }
                          if (!item.proyecto)
                          {
                            item.proyecto = [];
                          }
                          if (!item.url_imagen)
                          {
                            item.url_imagen = null;
                          }
                          if (!item.semblanzas || item.semblanzas === undefined)
                          {
                            item.semblanzas = [];
                          }
                          // Inicializar sub_linea_investigacion si no existe
                          if (item.sub_linea_investigacion === undefined) {
                            item.sub_linea_investigacion = null;
                          }
                          if (typeof item.orcidid === 'string') {
                            item.orcidid = item.orcidid.trim() || null;
                          } else if (item.orcidid === undefined || item.orcidid === '') {
                            item.orcidid = null;
                          }
                          item.orden = data.length + 1 ;
                          item.habilitado = item.habilitado === 'false' || item.habilitado === false ? false : true;
                          if (item.graduados === undefined || item.graduados === null) {
                            item.graduados = false;
                          } else {
                            item.graduados = item.graduados === 'false' || item.graduados === false ? false : true;
                          }
                          try {
                            const docRef = await addDoc(collection(db, "students"), item);
                            item.id = docRef.id;
                          } catch (error) {
                            notifyDashboardCrudError({ operation: DASHBOARD_CRUD.CREATE, entityName: TITLE, error });
                            return false;
                          }
                        }}
                        className="table table-light align-middle"
                        style={{ width: '100%', minWidth: '1400px' }}
                        headClassName="table-primary"
                        thClassName={{
                          proyecto: "col-6",
                          semblanzas: "col-3",
                        }}
                        inputClassName="w-100"
                        bodyClassName="sortable-items"
                        btnValidateClassName="btn btn-success p-0 m-0"
                        btnDeleteClassName="btn btn-danger py-0 px-1 m-0 mx-1"
                        btnCancelClassName="btn btn-secondary py-0 px-1 m-0 mx-1"
                        rowClassName="draggable"
                        insertClassName="not-draggable"
                        filler={<React.Fragment>&#8230;</React.Fragment>}
                      />
                    </React.Fragment>
                </div>
            </div>
        </div>
    </div>
  );
}
